import { Component, DestroyRef, OnInit } from '@angular/core';
import { API, ChannelFullDTO, MarkerDTO } from '../../../../services/api/api';
import { ActivatedRoute } from '@angular/router';
import { ErrorService } from '../../../../services/error.service';
import { Observable } from 'rxjs';
import { UtilService } from '../../../../services/util.service';
import { PermissionService } from '../../../../services/permission.service';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface ChannelMarkerPreview {
  backgroundColor: string;
  backgroundImage: string;
}

@Component({
  selector: 'app-channel-marker',
  templateUrl: './channel-marker.component.html',
  styleUrls: ['./channel-marker.component.scss'],
  standalone: false,
})
export class ChannelMarkerComponent implements OnInit {
  debounceTimer: ReturnType<typeof setTimeout> = null;

  sub: any;
  channelId: any;
  channel: ChannelFullDTO;
  markers: MarkerDTO[];

  fixedDimension = 324;

  previews = [
    'assets/img/channelpreview/preview01.png',
    'assets/img/channelpreview/preview02.png',
    'assets/img/channelpreview/preview03.png',
    'assets/img/channelpreview/preview04.png',
  ];

  selectedPreviews = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    private errorHandler: ErrorService,
    public util: UtilService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit() {
    this.sub = this.activatedRoute.parent.params.subscribe(params => {
      this.channelId = +params.channelID;
      this.api
        .ss_nm_Channel_retrieveById(this.channelId)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (e: ChannelFullDTO) => {
            this.channel = e;
            this.markers = e.markers;
            this.refreshPreviews();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    });
  }

  refreshPreviews() {
    while (this.markers?.length > this.selectedPreviews.length) {
      this.selectedPreviews.push({ backgroundColor: '#3E7CB1', backgroundImage: null });
    }
    while (this.selectedPreviews.length > this.markers.length) {
      this.selectedPreviews.pop();
    }
  }

  calculateWidth(marker?: MarkerDTO) {
    if (marker) {
      const ratio = marker?.resolution?.width / marker?.resolution?.height;
      if (marker?.resolution?.width > marker?.resolution?.height) {
        return this.fixedDimension;
      } else {
        return this.fixedDimension * ratio;
      }
    }
  }

  calculateHeight(marker?: MarkerDTO) {
    if (marker) {
      const ratio = marker?.resolution?.width / marker?.resolution?.height;
      if (marker?.resolution?.width > marker?.resolution?.height) {
        return this.fixedDimension / ratio;
      } else {
        return this.fixedDimension;
      }
    }
  }

  getPreviewByIndex(i: number): ChannelMarkerPreview {
    try {
      return this.selectedPreviews[i];
    } catch (err) {
      this.errorHandler.errorHandler(err);
    }

    return { backgroundColor: '#3E7CB1', backgroundImage: null };
  }

  getPreviewBackgroundColorByIndex(i: number): string {
    return this.getPreviewByIndex(i).backgroundColor;
  }

  getPreviewBackgroundImageByIndex(i: number): string {
    const img = this.getPreviewByIndex(i).backgroundImage;
    if (img) {
      return 'url(' + img + ')';
    }

    return ''; // null not possible due to error occurring
  }

  selectPreview(markerIndex: number, previewIndex: number) {
    if (previewIndex === -1) {
      this.selectedPreviews[markerIndex] = { backgroundColor: '#3E7CB1', backgroundImage: null };
    } else {
      this.selectedPreviews[markerIndex] = { backgroundColor: null, backgroundImage: this.previews[previewIndex] };
    }
  }

  addMarker() {
    let marker: MarkerDTO;
    marker = this.api.createDTO('MarkerDTO');
    marker.resolution = this.api.createDTO('ResolutionDTO');
    marker.resolution.width = 1;
    marker.resolution.height = 1;
    marker.name = 'New Marker';
    this.markers.push(marker);
    this.refreshPreviews();
    this.emitChange();
  }

  removeMarker(i: number) {
    const arr = [];
    for (let j = 0; j < this.markers.length; j++) {
      if (j !== i) {
        arr.push(this.markers[j]);
      }
    }

    this.markers = arr;
    this.refreshPreviews();
    this.emitChange();
  }

  emitChange() {
    this.channel.markers = this.markers;
    this.updateDebounced();
  }

  updateDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.update();
    }, 1000);
  }

  update() {
    let obs: Observable<ChannelFullDTO>;
    if (this.channel.id) {
      obs = this.api.ss_nm_Channel_updateById(this.channel);
    } else {
      obs = this.api.ss_nm_Channel_create(this.channel);
    }

    obs.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: ChannelFullDTO) => {
        this.channel = res;
      },
      error: error => {
        this.errorHandler.errorHandler(error);
      },
    });
  }
}
